(() => {
  const key = 'fxg_recently_viewed';
  const requests = new Map();
  const validHandle = (handle) => typeof handle === 'string' && /^[a-z0-9][a-z0-9-]*$/.test(handle);
  const readHistory = () => {
    try {
      const stored = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(stored) ? [...new Set(stored.filter(validHandle))].slice(0, 12) : [];
    } catch {
      return [];
    }
  };

  const current = document.querySelector('script[data-current-handle]')?.dataset.currentHandle;
  if (validHandle(current)) {
    try {
      localStorage.setItem(key, JSON.stringify([current, ...readHistory().filter((handle) => handle !== current)].slice(0, 12)));
    } catch {}
  }

  class CartHistoryRecommendations extends HTMLElement {
    connectedCallback() {
      this.timer = setTimeout(() => {
        this.initializeCarousel();
        this.load();
      });
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.listeners?.abort();
      this.resizeObserver?.disconnect();
    }

    initializeCarousel() {
      this.slider = this.querySelector('.cart-rec-slider');
      if (!this.slider) return;
      this.listeners = new AbortController();
      const signal = this.listeners.signal;
      this.slider.addEventListener('scroll', () => this.updateCarousel(), { passive: true, signal });
      this.querySelectorAll('[data-direction]').forEach((button) => {
        button.addEventListener('click', () => {
          const cards = [...this.slider.children];
          const index = this.currentIndex || 0;
          const next = Math.max(0, Math.min(cards.length - 1, index + Number(button.dataset.direction)));
          if (!cards[next]) return;
          this.slider.scrollBy({
            left: cards[next].getBoundingClientRect().left - cards[index].getBoundingClientRect().left,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
          });
        }, { signal });
      });
      this.resizeObserver = new ResizeObserver(() => this.updateCarousel());
      this.resizeObserver.observe(this.slider);
      this.updateCarousel();
    }

    updateCarousel() {
      if (!this.slider) return;
      const cards = [...this.slider.children];
      const left = this.slider.getBoundingClientRect().left;
      this.currentIndex = cards.reduce((closest, card, index) =>
        Math.abs(card.getBoundingClientRect().left - left) < Math.abs(cards[closest].getBoundingClientRect().left - left)
          ? index : closest, 0);
      const pagination = this.querySelector('.cart-rec-pagination');
      if (pagination) {
        pagination.hidden = cards.length <= 1;
        const text = `${this.currentIndex + 1} / ${cards.length}`;
        if (pagination.textContent !== text) pagination.textContent = text;
      }
      this.querySelectorAll('[data-direction]').forEach((button) => {
        button.hidden = cards.length <= 1;
        button.disabled = Number(button.dataset.direction) < 0
          ? this.currentIndex === 0 : this.currentIndex >= cards.length - 1;
      });
    }

    async load() {
      const history = readHistory();
      if (!history.length) return;
      const url = new URL(this.dataset.searchUrl, window.location.origin);
      url.searchParams.set('q', history.map((handle) => `handle:${handle}`).join(' OR '));
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[unavailable_products]', 'hide');
      url.searchParams.set('section_id', 'cart-history-recommendations');

      try {
        const requestKey = url.toString();
        if (!requests.has(requestKey)) {
          requests.set(requestKey, fetch(url).then((response) => {
            if (!response.ok) throw new Error('Unable to load cart history');
            return response.text();
          }).catch((error) => {
            requests.delete(requestKey);
            throw error;
          }));
        }
        const html = await requests.get(requestKey);
        if (!this.isConnected) return;
        const results = new DOMParser().parseFromString(html, 'text/html');
        const slider = this.querySelector('.cart-rec-slider');
        if (!slider || !results.querySelector('[data-cart-history-results]')) return;

        const excluded = new Set(this.dataset.cartHandles.split(',').filter(Boolean));
        const cards = [];
        const modals = [];
        const append = (container, recentlyViewed = false) => {
          if (!container) return;
          container.querySelectorAll('.cart-rec-card').forEach((card) => {
            const handle = card.dataset.productHandle;
            if (excluded.has(handle) || cards.length >= 20) return;
            excluded.add(handle);
            const copy = card.cloneNode(true);
            const label = copy.querySelector('.cart-rec-viewed-label');
            if (label) label.hidden = !recentlyViewed;
            cards.push(copy);
            const modalId = card.querySelector('modal-opener')?.dataset.modal.slice(1);
            const modal = modalId && container.querySelector(`#${CSS.escape(modalId)}`);
            if (modal) modals.push(modal.cloneNode(true));
          });
        };

        history.forEach((handle) => append(results.querySelector(`template[data-history-handle="${handle}"]`)?.content, true));
        history.forEach((handle) => append(results.querySelector(`template[data-related-handle="${handle}"]`)?.content));
        append(slider);
        if (!cards.length) return;

        document.querySelectorAll('[data-cart-history-modal]').forEach((modal) => {
          if (!modal.hasAttribute('open')) modal.remove();
        });
        modals.forEach((modal) => {
          if (document.getElementById(modal.id)) return;
          modal.setAttribute('data-cart-drawer-modal', '');
          modal.setAttribute('data-cart-history-modal', '');
          document.body.appendChild(modal);
        });
        slider.replaceChildren(...cards);
        slider.scrollLeft = 0;
        this.hidden = false;
        this.updateCarousel();
      } catch {}
    }
  }

  if (!customElements.get('cart-history-recommendations')) {
    customElements.define('cart-history-recommendations', CartHistoryRecommendations);
  }
})();
